<template>
  <div class="divide-y divide-gray-200">
    <LayupSpinner v-if="isLoading" />
    <div v-else>
      <div v-if="errorMessage" class="rounded-md w-full bg-red-100 px-4 py-3 text-sm text-red-700">
        {{ errorMessage }}
      </div>
      <div class="flex items-center cursor-pointer p-4" @click="back">
        <img src="@/assets/icons/backArrow.svg" class="w-5 h-5" />
        <div class="flex-grow text-lg font-semibold text-center text-black">DebiCheck</div>
      </div>
      <form @submit.prevent="onSubmit">
        <div class="space-y-2 p-4 sm:p-6">
          <div class="space-y-4">
            <h1 class="text-xl font-semibold text-black">Personal Information</h1>
            <div class="space-y-2">
              <FormField v-slot="{ componentField }" name="firstName">
                <FormItem>
                  <FormLabel>First Name</FormLabel>
                  <FormControl>
                    <Input type="text" placeholder="John" v-bind="componentField" data-testid="first_name" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              </FormField>
            </div>
            <div class="space-y-2">
              <FormField v-slot="{ componentField }" name="surname">
                <FormItem>
                  <FormLabel>Surname</FormLabel>
                  <FormControl>
                    <Input type="text" placeholder="Doe" v-bind="componentField" data-testid="surname" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              </FormField>
            </div>

            <div class="space-y-2">
              <FormField v-slot="{ componentField }" name="cellNumber">
                <FormItem>
                  <FormLabel>Mobile Number</FormLabel>
                  <FormControl>
                    <Input type="text" placeholder="01110000000" v-bind="componentField" data-testid="cell_number" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              </FormField>
            </div>

            <div class="space-y-2">
              <FormField v-slot="{ componentField }" name="identityType">
                <FormItem>
                  <FormLabel>Identification Type</FormLabel>
                  <Select v-bind="componentField" data-testid="identity_type">
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Identity Type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem v-for="type in identificationSelection" :key="type.value" :value="type.value">
                          {{ type.text }}
                        </SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              </FormField>
            </div>
            <div class="space-y-2">
              <FormField v-slot="{ componentField }" name="identificationNumber">
                <FormItem>
                  <FormLabel>ID Number</FormLabel>
                  <FormControl>
                    <Input type="text" placeholder="8504121234081" v-bind="componentField" data-testid="id_number" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              </FormField>
            </div>
          </div>

          <div class="space-y-4">
            <h1 class="text-xl font-semibold text-black">Payment Information</h1>
            <div class="grid grid-cols-2 gap-4">
              <div class="space-y-2 col-span-1">
                <FormField v-slot="{ componentField }" name="billingDay">
                  <FormItem>
                    <FormLabel>Billing Date</FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        placeholder="2020-12-28"
                        v-bind="componentField"
                        data-testid="billing_date"
                        disabled
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                </FormField>
              </div>
              <div class="space-y-2 col-span-1">
                <FormField v-slot="{ componentField }" name="instalmentAmount">
                  <FormItem>
                    <FormLabel>Amount</FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        placeholder="500"
                        v-bind="componentField"
                        data-testid="instalment_amount"
                        disabled
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                </FormField>
              </div>
            </div>
            <div class="grid grid-cols-3 gap-4">
              <div class="space-y-2 col-span-2">
                <FormField v-slot="{ componentField }" name="bankName">
                  <FormItem>
                    <FormLabel>Bank Name</FormLabel>
                    <Select v-bind="componentField" data-testid="bank_name">
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Bank Name" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectGroup>
                          <SelectItem v-for="bank in bankSelection" :key="bank.value" :value="bank.value">
                            {{ bank.text }}
                          </SelectItem>
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                </FormField>
              </div>

              <div class="space-y-2">
                <FormField v-slot="{ componentField }" name="branchCode">
                  <FormItem>
                    <FormLabel>Branch Code</FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        placeholder="632005"
                        v-bind="componentField"
                        data-testid="branch_code"
                        disabled
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                </FormField>
              </div>
            </div>

            <div class="grid grid-cols-5 gap-4">
              <div class="space-y-2 col-span-2">
                <FormField v-slot="{ componentField }" name="accountType">
                  <FormItem>
                    <FormLabel>Account Type</FormLabel>
                    <Select v-bind="componentField" data-testid="account_type">
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Account Type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectGroup>
                          <SelectItem v-for="type in accountTypeSelection" :key="type.value" :value="type.value">
                            {{ type.text }}
                          </SelectItem>
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                </FormField>
              </div>

              <div class="space-y-2 col-span-3">
                <FormField v-slot="{ componentField }" name="accountNumber">
                  <FormItem>
                    <FormLabel>Account Number</FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        placeholder="8704123456"
                        v-bind="componentField"
                        data-testid="account_number"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                </FormField>
              </div>
            </div>
          </div>
        </div>
        <div class="p-2 sm:p-6">
          <div class="flex text-left px-2" v-if="ENVIRONMENT !== 'production'">
            <input
              type="checkbox"
              v-model="allowSimulation"
              name="acceptTermsAndConditions"
              class="rounded mr-2 focus:bg-brand-300 focus:ring-brand-500 focus:shadow-outline focus:ring-2"
              id="simulation-checkbox"
              data-testid="simulation"
            />
            <label class="break-words text-sm text-gray-500" for="simulation-checkbox"> Simulation </label>
          </div>
          <div class="flex text-left px-2">
            <input
              type="checkbox"
              v-model="acceptTermsAndConditions"
              name="acceptTermsAndConditions"
              class="rounded mr-2 focus:bg-brand-300 focus:ring-brand-500 focus:shadow-outline focus:ring-2"
              id="terms-checkbox"
              data-testid="accept_terms_conditions"
            />
            <label class="break-words text-sm text-gray-500" for="terms-checkbox">
              I will allow LayUp to share this information with the relevant banks so that they can set up my debit
              order.
            </label>
          </div>
          <Button
            class="w-full mt-2"
            type="submit"
            :disabled="!acceptTermsAndConditions || isLoading"
            data-testid="submit_button"
            >Submit</Button
          >
        </div>
      </form>
    </div>
  </div>
</template>
<script setup lang="ts">
import LayupSpinner from '@/components/common/LayupSpinner.vue';
import { Button } from '@/components/ui/button';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ENVIRONMENT } from '@/config';
import { NetworkError } from '@/errors';
import { getMe } from '@/services/auth';
import { fetchBanks } from '@/services/banks';
import { createMandate } from '@/services/debiCheck';
import { getOrderByIdWithPlansAndPayments } from '@/services/orders';
import { useWidgetStore } from '@/stores/widget';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { nextPayment } from '@/utils/paymentPlan.utils';
import { toTypedSchema } from '@vee-validate/zod';
import dayjs from 'dayjs';
import { useForm } from 'vee-validate';
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { z } from 'zod';
import {
  ModelDebiCheckAccountTypeEnum,
  ModelDebiCheckIdentificationTypeEnum,
  ModelDebiCheckMediumEnum,
  type ModelDebiCheckRequest,
  type ModelPaymentPlanResponse,
} from '@layup/api-sdk';
import { currency } from '../../../utils/format.utils';

const router = useRouter();
const isLoading = ref(false);
const errorMessage = ref('');

const widgetStore = useWidgetStore();
const acceptTermsAndConditions = ref(false);
const allowSimulation = ref(false);

const bankSelection = ref([] as any);

const debicheckFormSchema: any = toTypedSchema(
  z.object({
    identificationNumber: z.string().min(1, 'ID number is required'),
    firstName: z.string().min(1, 'First name is required'),
    surname: z.string().min(1, 'Surname is required'),
    cellNumber: z.string().min(1, 'Cell number is required'),
    bankName: z.number().min(0, 'Bank name is required'),
    accountNumber: z.string().min(1, 'Account number is required'),
    branchCode: z.string().min(1, 'Branch code is required'),
    instalmentAmount: z.string().min(1, 'Instalment amount is required'),
    billingDay: z.string().min(1, 'Billing day is required'),
    identityType: z.string().min(1, 'Identity type is required'),
    accountType: z.string().min(1, 'Account type is required'),
  }),
);

const form = useForm({
  validationSchema: debicheckFormSchema,
});

const splitCamelCaseToWords = (text: string): string => {
  return text.replace(/([a-z])([A-Z])/g, '$1 $2');
};

const identificationSelection = computed(() =>
  Object.keys(ModelDebiCheckIdentificationTypeEnum).map((k, i) => ({
    value: i.toString(),
    text: splitCamelCaseToWords(k),
  })),
);

const accountTypeSelection = computed(() =>
  Object.keys(ModelDebiCheckAccountTypeEnum).map((k, i) => ({
    value: i.toString(),
    text: splitCamelCaseToWords(k),
  })),
);

watch(
  () => form.values.bankName,
  (bankNameNew) => {
    const selectedBank = bankSelection.value.find((b: any) => b.value === bankNameNew);
    if (selectedBank) {
      form.setValues({
        branchCode: selectedBank.branchCode,
      });
    }
  },
);

const handleError = (error: any, context: string = 'fetch') => {
  const baseMessage =
    context === 'fetch'
      ? 'Could not fetch data. Please try again later.'
      : 'Could not create debi check. Please try again later.';

  errorMessage.value = error?.response?.data || baseMessage;

  if (error instanceof NetworkError) {
    createWarning(error);
    addMetric('debi-check', {
      status: 'network_error',
      channel: 'widget',
      method: 'eft',
    });
  } else {
    createException(error);
    addMetric('debi-check', {
      status: 'error',
      channel: 'widget',
      method: 'eft',
    });
  }
};

const onSubmit = async () => {
  const { values, errors, valid } = await form.validate();

  // Need to manually check errors because for some reason vee-validate doesn't set errors on the form when submitted even if valid is false
  if (Object.keys(errors).length > 0) {
    Object.entries(errors).forEach(([key, value]) => {
      form.setFieldError(key as keyof typeof errors, value);
    });
    return;
  }
  if (valid) {
    try {
      isLoading.value = true;
      const request: ModelDebiCheckRequest = {
        name: `${values?.firstName} ${values?.surname}`,
        identification: values?.identificationNumber,
        accountType: Object.values(ModelDebiCheckAccountTypeEnum)[Number(values?.accountType)],
        branchCode: values?.branchCode,
        identificationType: Object.values(ModelDebiCheckIdentificationTypeEnum)[Number(values?.identityType)],
        paymentPlanId: widgetStore.id,
        simulation: allowSimulation.value,
        contact: [{ medium: ModelDebiCheckMediumEnum.PhoneNumber, value: values?.cellNumber }],
        accountNumber: values?.accountNumber,
      };

      widgetStore.mandateRequest = request;

      const response = await createMandate(request);
      await router.push({
        name: 'DebiCheckMandateConfirmation',
        params: {
          id: response._id,
        },
      });
    } catch (error: any) {
      handleError(error, 'submit');
    } finally {
      isLoading.value = false;
    }
  }
};

const back = () => {
  router.back();
};

const init = async () => {
  try {
    isLoading.value = true;

    const [order, user, bankResponse] = await Promise.all([
      getOrderByIdWithPlansAndPayments(widgetStore.order._id!),
      getMe(),
      fetchBanks(),
    ]);

    widgetStore.order = order;
    const paymentPlan = widgetStore.getPaymentPlan() as ModelPaymentPlanResponse;
    const nextDuePayment = nextPayment(paymentPlan);

    if (nextDuePayment) {
      form.setValues({
        instalmentAmount: currency(nextDuePayment.amount),
        billingDay: dayjs(nextDuePayment.due).format('YYYY-MM-DD'),
        identityType: '0',
        accountType: '0',
      });
    }

    if (user) {
      const [firstNameValue, surnameValue] = user.name?.split(' ') || [];
      form.setValues({
        firstName: firstNameValue,
        surname: surnameValue,
        cellNumber: user.cellNumber,
        identificationNumber: user.idNumber,
      });

      if (user.bankDetails?.bankName) {
        const selectedBank = bankResponse.banks?.find((b) => b.name === user.bankDetails?.bankName?.value);
        if (selectedBank) {
          form.setValues({
            bankName: bankSelection.value.findIndex((b: any) => b.name === selectedBank.name),
            branchCode: selectedBank.branchCode,
          });
        }
      }
    }

    bankSelection.value =
      bankResponse.banks?.map((b, i) => ({
        value: i,
        text: b.displayName,
        branchCode: b.branchCode,
        name: b.name,
      })) || [];

    if (bankSelection.value.length > 0) {
      form.setValues({
        bankName: bankSelection.value[0].value,
        branchCode: bankSelection.value[0].branchCode,
      });
    }

    // This block handles pre-filling form data when a DebiCheck mandate has been rejected
    // It populates the form fields with the previous mandate request information
    // allowing the user to review and update their details for resubmission
    // This ensures a smoother correction process for failed mandates
    // This will make sure that correct mandate details for the correct order are used
    if (widgetStore.mandateRequest?.accountNumber && widgetStore.mandateRequest?.paymentPlanId === widgetStore.id) {
      form.setValues({
        accountNumber: widgetStore.mandateRequest.accountNumber,
        accountType: Object.values(ModelDebiCheckAccountTypeEnum)
          .findIndex((a) => a === widgetStore.mandateRequest?.accountType)
          .toString(),
        identityType: Object.values(ModelDebiCheckIdentificationTypeEnum)
          .findIndex((a) => a === widgetStore.mandateRequest?.identificationType)
          .toString(),
        branchCode: widgetStore.mandateRequest.branchCode,
        firstName: widgetStore.mandateRequest.name?.split(' ')[0],
        surname: widgetStore.mandateRequest.name?.split(' ')[1],
        cellNumber: widgetStore.mandateRequest.contact?.[0]?.value,
        identificationNumber: widgetStore.mandateRequest.identification,
        instalmentAmount: currency(paymentPlan.payments?.[0]?.amount || 0),
        billingDay: dayjs(paymentPlan.payments?.[0]?.due).format('YYYY-MM-DD'),
        bankName: bankSelection.value.findIndex((b: any) => b.branchCode === widgetStore.mandateRequest?.branchCode),
      });
    }
  } catch (error: any) {
    handleError(error);
  } finally {
    isLoading.value = false;
  }
};

init();
</script>
